import {
  AUDIO_FILE,
  AUDIO_PRESENTER,
  CAMERA_PRESENTER,
  IMAGE_FILE,
  MEDIA,
  SCREEN_PRESENTER,
  VIDEO_FILE,
  PDF_FILE
} from '@shoflo/polymer';
import _, { find, pick } from 'underscore';
import moment from 'moment';

import { updateSource } from 'actions/Broadcast';
import { setMultipleDesiredAttendeeMuteStatuses } from '@shoflo/studio-redux/actions';
import { PLAY_DELAY } from 'constants/MediaPlayer';
import { BANNER_PLACEHOLDERS } from 'constants/Banners';
import { PENDING, SCREENSHARE } from 'constants/KnockTypes';
import { isLayout } from '@studio/program/sceneLayout/sceneLayoutSelector';
import {
  FEATURED_MEDIA,
  FEATURED_PRESENTER,
  FEATURED_SCREEN,
  GRID
} from 'constants/SceneLayouts';
import { S3SourceTypes } from 'constants/SourceItem';
import { getMediaSourceValueObject } from 'actions/utils';
import { getMinutesDifference } from 'utils/utils';
import { SIGNED_URLS_CACHE_TIMEOUT_VALUE } from '../constants';
import { STOCK_MEDIA } from '@studio/constants/Sources';

/** Check that the item is not undefined or null */
export const isItemValid = item => !!item;

/**
 * Check if Audio Source
 */
export const isAudioSource = source => source && source.type === AUDIO_FILE;

/**
 * Check if Video Source
 */
export const isVideoSource = source => source && source.type === VIDEO_FILE;

/** Check if source is an image source */
export const isImageSource = source => source && source.type === IMAGE_FILE;

/**
 * Check if Camera presenter
 */
export const isCameraPresenter = source =>
  source && source.type === CAMERA_PRESENTER;

export const isScreenPresenter = source =>
  source && source.type === SCREEN_PRESENTER;
/**
 * Check if Media with an Audio source
 */
export const isAudioVideoSource = source =>
  [VIDEO_FILE, AUDIO_FILE].includes(source.type);

/**
 * CHeck if Media source
 */
export const isMediaSource = source =>
  [VIDEO_FILE, AUDIO_FILE, IMAGE_FILE, MEDIA, PDF_FILE].includes(source.type);

export const isMediaSourceExcludeAudio = source =>
  [VIDEO_FILE, IMAGE_FILE, MEDIA].includes(source.type);

export const UNASSIGNED = 'Unassigned';

export const EMPTY_THUMBNAIL = 'EmptyThumbnail';

/**
 * Check if presenter source
 */
export const isPresenterSource = source =>
  [CAMERA_PRESENTER, SCREEN_PRESENTER].includes(source.type);

export const isAssignedPresenterSource = source =>
  isPresenterSource(source) && source.value !== UNASSIGNED;

/**
 * Check if source.value is type object
 */
export const isObjectSourceValue = sourceValue =>
  typeof sourceValue === 'object';

/**
 * Get Source Value changes
 */
export const getSourceValueChanges = (source, serverTimeDelta) => {
  const { value: sourceValue, hidden } = source;

  if (isAudioVideoSource(source)) {
    const isPlaying =
      sourceValue.isPlaying && hidden ? true : !sourceValue.isPlaying;

    return {
      ...sourceValue,
      seek: 0,
      // Some sources have isPlaying value already set to true
      isPlaying,
      startedOn: getStartedOn(isPlaying, serverTimeDelta)
    };
  }

  return sourceValue;
};

export const getSourceFromProgramLayers = (layers, predicate) =>
  layers?.map(layer => layer?.source).filter(predicate);

export const getIsStagedSourcesActive = stagedSourcesMap =>
  Object.keys(stagedSourcesMap).reduce(
    (acc, sId) => acc || stagedSourcesMap[sId],
    false
  );

export const getMediaSourceElement = sourceId => {
  return document.getElementById(`media_player_${sourceId}`);
};

export const getStartedOn = (isPlaying, serverTimeDelta = 0) =>
  isPlaying ? moment().utc().unix() - serverTimeDelta + PLAY_DELAY : undefined;

/**
 * Reset unhidden video source to seek position 0
 *
 * @param liveSources
 * @param sources
 * @param dispatch
 * @returns {*}
 */
export const resetNotLiveVideoSource = (
  liveSources,
  sources,
  serverTimeDelta,
  dispatch
) => {
  let sourceToUpdate = null;

  const resetSources = sources.map(source => {
    const { _id, type, hidden } = source;
    if (type === VIDEO_FILE && !hidden && !liveSources.includes(_id)) {
      source.value = {
        ...source.value,
        seek: 0,
        isPlaying: true,
        startedOn: getStartedOn(true, serverTimeDelta)
      };
      sourceToUpdate = source;
      return source;
    }
    return source;
  });

  if (sourceToUpdate) {
    const { _id, value } = sourceToUpdate;
    dispatch(updateSource(_id, { value }));
  }

  return resetSources;
};

/**
 * This function returns the social icon from RTMP url
 * @param {String} url to screen
 * @return {Object} returns the vendor name of the stream source
 */

const streamNames = [
  'facebook',
  'vimeo',
  'youtube',
  'bcovelive',
  'twitter',
  'twitch',
  'linkedin',
  'wowza',
  'streaming',
  'play',
  'stop',
  'recording',
  'inactiveStream',
  'activeRecording',
  'inactiveRecording'
];
export const getStreamSource = url => {
  if (!url) {
    return 'recordingEllipse';
  }

  let source = 'rtmp';

  if (url) {
    streamNames.forEach(name => {
      if (url.includes(name)) {
        source = name;
      }
    });
  }

  return source;
};

/**
 * This function returns difference in time from given date param with current date
 * @param {String} startedAt the start date in string
 * @return {Number} returns the difference from now and startedAt
 */
export const getElapsedTime = startedAt => {
  const currentServerTime = moment(startedAt).valueOf();
  const currentLocalTime = moment().valueOf();
  const elapsedTime = currentLocalTime - currentServerTime;
  return elapsedTime;
};

/**
 * This function groups an array by _id attribute as key to object mapping
 * @param {String} array the start date in string
 * @return {Number} returns the difference from now and startedAt
 */
export const groupById = array => {
  return array.reduce((accumulator, currentObject) => {
    const { _id } = currentObject;
    accumulator[_id] = currentObject;
    return accumulator;
  }, {});
};

export const isSourceInProgram = (layers = [], sourceId) => {
  // Check if source exists in program layer
  const [layerSource] = layers
    .filter(layer => isItemValid(layer))
    .filter(layer => layer?.source?._id === sourceId);

  return !!layerSource;
};

/**
 * Returns the source value from the program layers if the source exists there,
 * falling back to the source's own value. This preserves playback state
 * (seek, startedOn, isPlaying) that may differ from broadcastSources.
 */
export const getProgramSourceValue = (layers, source) => {
  const programSource = layers?.find(l => l.source?._id === source._id)?.source;
  return programSource?.value || source.value;
};

export const getAudioSourceInProgram = (layers = []) => {
  // Check if audio source exists in program layer
  const audioSource = layers.find(layer => isAudioSource(layer.source));

  return audioSource?.source;
};

export const getUnassignedMediaSources = sources => {
  const unAssignedMediaSources = sources.filter(source => {
    return source.type === MEDIA && source.value === '';
  });

  return unAssignedMediaSources;
};

// we are creating and audioPresenter for each screenPresenter or cameraPresenter that is unmuted
export const attachAudioPresenterSources = (sources, attendeeMuteStatus) => {
  const unMutedPresenters =
    (sources &&
      sources.filter(s => {
        const sourceValue = getSourceValue(s);
        return (
          s &&
          [CAMERA_PRESENTER, SCREEN_PRESENTER].includes(s.type) &&
          s.value &&
          !attendeeMuteStatus[sourceValue]
        );
      })) ||
    [];
  // This should make sure that duplicate audio presenter is not added to program
  const audioPresenterSources = [];
  unMutedPresenters.forEach(source => {
    const sourceValue = getSourceValue(source);
    const audioSourceIndex = sources.findIndex(
      s => s.value === source._id && s.type === AUDIO_PRESENTER
    );
    if (audioSourceIndex <= -1) {
      audioPresenterSources.push({
        ...source,
        _id: sourceValue,
        type: AUDIO_PRESENTER,
        value: source._id
      });
    }
  });
  return sources ? sources.concat(audioPresenterSources) : [];
};

/**
 * This will make sure all audio presenter source in program has
 * a matching source from either camera / screen presenter
 * @param {Object} layers takes a layers of a program to validate
 * @param {Object} sources from broadcast sources
 * @return {Object} returns an updated program object that has been validated
 */
export const reValidateAudioPresentersInProgramLayers = (layers, sources) => {
  const presenters = sources.filter(s =>
    [CAMERA_PRESENTER, SCREEN_PRESENTER].includes(s.type)
  );
  return layers.filter(
    layer =>
      presenters.findIndex(
        presenter => layer.source.value === presenter.value
      ) > -1 ||
      ![CAMERA_PRESENTER, SCREEN_PRESENTER, AUDIO_PRESENTER].includes(
        layer.source.type
      )
  );
};

/**
 * This will add audio sources to program for any unMuted presenter given the attendeeId
 * @param {Object} layers takes a layers of a program to validate
 * @param {Object} sources with audio presenter sources
 * @return {Object} returns an updated program object that has been validated
 */
export const addAudioPresentersToProgramLayer = (layers, sources) => {
  const filterAudioPresenter = sources.filter(
    s => s && s.type === AUDIO_PRESENTER
  );
  const audioPresenterLayer = filterAudioPresenter.map(source => {
    return {
      _id: source._id,
      source,
      layer: {}
    };
  });
  layers = layers.concat(audioPresenterLayer);
  return layers;
};

/**
 * This is expected to retain the current banner in state.
 * @param {Object} withAudioPresenterLayer a just updated program object that has the audioPresenter validated
 * @param {Object} program current program in state
 * @return {Object} returns an updated program object that has the correct banner layer
 */

export const addUnChangedBannerLayerToProgram = (
  withAudioPresenterLayer,
  program
) => {
  return withAudioPresenterLayer.map(layer => {
    const { source } = layer;
    // Updates the withAudioPresenterLayer with the correct program layer
    if (source && source?.type === 'banner') {
      const programLayer = program?.layers.find(
        programLayer => programLayer?.source._id === source._id
      );

      /** What happens if programLayer is undefined? */
      return programLayer || layer;
    }

    return layer;
  });
};

/**
 * @description This will generate mock banner given the banner template type
 * @param {string} templateType
 * @param {boolean} hidden
 */
export function generateBanner(templateType, hidden = false) {
  const banner = {
    layer: {},
    source: {
      hidden,
      value: {
        templateVars: {},
        templateType,
        markup:
          '<section><h1>Presenter name</h1><h2>Presenter title</h2></section>'
      }
    }
  };

  return banner;
}

/**
 * @description Sanitize html from string
 * @param {string} str
 * @returns String return sanitised string
 */
export const sanitizeHTML = function (str) {
  if (!str) {
    return '';
  }
  return str.replace(/[^\w. ]/gi, function (c) {
    return '&#' + c.charCodeAt(0) + ';';
  });
};

/**
 * @description Formats presenter banner markup
 * @param {string} title
 * @param {string} description
 * @param {string} markup
 * @returns Object
 */
export function formatBannerMarkup(title, description, markup) {
  let bannerStyle = {
    height: '40px',
    position: 'absolute',
    bottom: '5px',
    left: '5px'
  };
  let titleStyle = {};
  let subTitleStyle = {
    paddingBottom: '6px'
  };
  let bannerMarkup = markup;
  // Replace title placeholder with actual presenter title
  bannerMarkup = bannerMarkup.replace(
    BANNER_PLACEHOLDERS.title,
    sanitizeHTML(title)
  );

  if (description) {
    // Replace description placeholder with actual presenter description
    bannerMarkup = bannerMarkup.replace(
      BANNER_PLACEHOLDERS.description,
      sanitizeHTML(description)
    );
  } else {
    bannerStyle.height = '27px';

    titleStyle = {
      paddingTop: '5px'
    };
    /**
     * Remove H2 tag `Presenter title` since we don't
     * have source description to display here.
     */
    bannerMarkup = removeH2FromPresenterBannerMarkup(bannerMarkup);
  }
  return {
    bannerStyle,
    bannerMarkup,
    titleStyle,
    subTitleStyle
  };
}

/**
 * @description Returns a source value. if the value is unassigned,
 * it returns a concatenated value with the source id
 * @param {Object} source
 * @returns String
 */
export function getSourceValue(source) {
  let sourceValue = source.value;
  if (sourceValue === UNASSIGNED) {
    sourceValue = `${UNASSIGNED}-${source._id}`;
  }
  return sourceValue;
}

/**
 * @description This passes every scene and returns an updated scenes array
 * trimmed enough with the valid structure for an api update call.
 * @param {Object} scenes
 * @returns Array
 */
export function filteredScenesForUpdate(scenes) {
  return scenes.map(scene => {
    // remove duplicate source layers
    return pick(
      {
        ...scene,
        layers: scene.layers.map(l => {
          return {
            ...l,
            source: pick(l.source, [
              '_id',
              'title',
              'type',
              'value',
              'hidden',
              'order',
              'thumbnail',
              'description'
            ])
          };
        })
      },
      ['_id', 'description', 'title', 'layers', 'style', 'thumbnail', 'layout']
    );
  });
}

/**
 * Returns the layout for the scene
 * @param {Object} scene
 * @return {{id: *, title: *}}
 */
export const getSceneLayout = scene => {
  let sceneLayout = {
    id: scene.layout,
    title: scene.title
  };
  // Change featured layout to grid when no featured source exist to avoid assigning placeholder default
  const isFeatured =
    isLayout(sceneLayout, FEATURED_SCREEN) ||
    isLayout(sceneLayout, FEATURED_MEDIA) ||
    isLayout(sceneLayout, FEATURED_PRESENTER);
  if (isFeatured) {
    const hasFeaturedSource = scene.layers.some(({ layer, source }) => {
      let isAFeaturedFile = [
        IMAGE_FILE,
        VIDEO_FILE,
        MEDIA,
        SCREEN_PRESENTER
      ].includes(source.type);
      return (
        isAFeaturedFile &&
        (layer.isContent === undefined || layer.isContent === true)
      );
    });
    if (!hasFeaturedSource) {
      sceneLayout = GRID;
      scene.layout = sceneLayout.id;
      scene.title = sceneLayout.title;
    }
  }
  return sceneLayout;
};

/**
 * Use to determine if the current knock type is a duplicate
 * @param {*} knock
 * @param {*} receivedKnock
 * @returns boolean
 */
export const isDuplicateRequest = (knock, receivedKnock) =>
  knock.presenterId === receivedKnock.presenterId &&
  knock.type === receivedKnock.type &&
  (knock.status || receivedKnock.status) === PENDING;

/**
 * Util function to check if a knock status is pending
 * @param {*} kn
 * @returns boolean
 */
export const isPendingKnock = kn => kn.status === PENDING;

/**
 * Check if knock is a director request type
 * Director request is one of screenshare/canScreenShare,
 * @param {*} requestType
 * @returns boolean
 */
export const isDirectorRequest = requestType =>
  [SCREENSHARE].includes(requestType);

/**
 * @description Get camera sources from program layers
 * @param {[]} layers
 * @returns []
 */
export const getLiveCameraSources = layers =>
  layers.filter(
    ({ source }) =>
      source.type === CAMERA_PRESENTER && source.value !== UNASSIGNED
  );

/**
 * Knock request unassigned presenter filters
 */
export const isUnassigned = source => source.value === UNASSIGNED;

/**
 * Knock request unassigned presenter filters
 */
export const isInitializing = source => source.isInitializing === true;

/** Predicate to check that the source is unassigned and disconnected */
export const isDisconnected = source =>
  source.value !== UNASSIGNED && source.disconnected;

/** Checks that the source is assigned and not in the attendee tile state */
export const isAvailableSource = (source, attendeeIds) =>
  source.value !== UNASSIGNED && !attendeeIds.includes(source.value);

/**
 * @param {Array} sourceLayers The source layers
 * @param {Array} destinationLayers The destination layers
 * @return {Boolean}
 */
export const hasSameAudioVideoSource = (
  sourceLayers,
  destinationLayers = []
) => {
  const sourceLayer = sourceLayers?.find(({ source }) =>
    isAudioVideoSource(source)
  );
  const destinationLayer = destinationLayers
    .filter(layer => isItemValid(layer))
    .find(({ source }) => isAudioVideoSource(source));
  return (
    sourceLayer &&
    destinationLayer &&
    sourceLayer?.source?._id === destinationLayer?.source?._id
  );
};

/**
 @description Removes `<H2/>` from presenter banner markup
 @param {string} markup
 */
export function removeH2FromPresenterBannerMarkup(markup) {
  const removeH2 = /<h2[^>]*>[^</]*<\/h2>/gi;

  return markup?.replace(removeH2, '');
}

let sourcesSignedUrlCache = {
  lastUpdated: new Date().getTime()
};

/**
 Returns a single source with signed url
 @param {{}} source
 @param {{}} apiClient
 @param {{}} forceFetch - Force fetch the signed URL by skipping cache
 @returns {{}} source with signed url.
 */
export const getSignedUrl = async (source, apiClient, forceFetch = false) => {
  if (
    !source?.value ||
    isPresenterSource(source) ||
    source?.value?.type === STOCK_MEDIA
  ) {
    return source;
  }

  const value = getMediaSourceValueObject(source.value);

  const { eventDocId } = value;

  let signedUrl = sourcesSignedUrlCache[eventDocId];

  if ((!signedUrl || forceFetch) && source.team && source.event) {
    const { url } = await apiClient.eventDoc.getSignedUrl(
      source.team,
      source.event,
      eventDocId
    );

    signedUrl = url;

    sourcesSignedUrlCache[eventDocId] = url;
  }

  return { ...source, value: { ...value, url: signedUrl } };
};

/**
 Maps a list of sources to signed urls
 @param {[]} sources
 @param {{}} apiClient
 @param {string} teamId
 @param {string} eventId
 */
export const getSourcesSignedUrl = async (
  sources,
  apiClient,
  teamId,
  eventId
) => {
  const minutes = getMinutesDifference(
    sourcesSignedUrlCache.lastUpdated,
    new Date().getTime()
  );
  const isValidCache = minutes < SIGNED_URLS_CACHE_TIMEOUT_VALUE;

  const eventDocIds = sources?.reduce((accumulator, source) => {
    const { value, type } = source;

    if (!S3SourceTypes.includes(type)) {
      return accumulator;
    }
    const { eventDocId } = getMediaSourceValueObject(value);

    if (!eventDocId || value?.type === STOCK_MEDIA) {
      return accumulator;
    }

    const signedUrl = sourcesSignedUrlCache[eventDocId];

    if (signedUrl && isValidCache) {
      return accumulator;
    }

    accumulator.push(eventDocId);

    return accumulator;
  }, []);

  if (eventDocIds?.length) {
    const { urls } = await apiClient.eventDoc.getSignedUrls(teamId, eventId, {
      eventDocs: eventDocIds
    });

    // update sourcesSignedUrlCache including last updated timestamp.
    sourcesSignedUrlCache = {
      ...sourcesSignedUrlCache,
      ...urls,
      lastUpdated: new Date().getTime()
    };
  }

  const sourcesWithSignedUrls = sources?.map(source => {
    const { value, type } = source;

    if (!S3SourceTypes.includes(type)) {
      return source;
    }

    // Convert to source object for legacy media files which has value as string
    const valueObj = getMediaSourceValueObject(value);

    const { eventDocId } = valueObj;

    if (!eventDocId || value?.type === STOCK_MEDIA) {
      return source;
    }

    const signedUrl = sourcesSignedUrlCache[eventDocId];

    return {
      ...source,
      value: {
        ...valueObj,
        url: signedUrl
      }
    };
  });

  return sourcesWithSignedUrls;
};

const isFulfilled = status => status === 'fulfilled';

/**
 @param {[]} response
 */
export const getSourceFromResponse = response => {
  return response.reduce((memo, item) => {
    const { status, value } = item;

    return isFulfilled(status) ? [...memo, value] : memo;
  }, []);
};

/**
 * Gets the layers in a scene or program
 * @param {*} program scene/program
 * @returns Layer[] Array of layers
 */
export const getProgramLayers = program => {
  const layers = program?.layers || [];

  return layers?.filter(layer => isItemValid(layer));
};

export const withAttendeeCameraStatus = (
  program,
  attendeeCameraStatus = {}
) => {
  const currentLayers = getProgramLayers(program).map(
    ({ layer, source, ...rest }) => {
      const currentLayer = {
        source,
        layer,
        ...rest
      };
      if (
        isPresenterSource(source) &&
        typeof attendeeCameraStatus[source.value] === 'boolean'
      ) {
        return {
          ...currentLayer,
          layer: {
            ...layer,
            isCameraOn: attendeeCameraStatus[source.value]
          }
        };
      }
      return currentLayer;
    }
  );
  return {
    ...program,
    layers: currentLayers
  };
};

/** Reducer util function to find and update broadcast sources
 *
 * Implementation update is to take account of the order of sources in the array
 * as it will have a direct impact on the program when sources are reordered.
 * */
export function findAndUpdate(oldSources, updatedSources) {
  return [...oldSources].reduce(
    (a, c) => {
      const updatedSource = find(updatedSources, us => us._id === c._id);
      if (!updatedSource) {
        a.push(c);
      }
      return a;
    },
    [...updatedSources]
  );
}

/** Reducer util function to find sources with disconnected state
 *
 * Implementation update is to take account of the order of sources in the array
 * as it will have a direct impact on the program when sources are reordered.
 * */
export function sourcesWithPreviousDisconnectedState(newSources, oldSources) {
  return newSources.map(s => {
    const oldSource = _.find(oldSources, oldSource => oldSource._id === s._id);
    if (oldSource)
      return {
        ...s,
        disconnected: oldSource.disconnected,
        isInitializing: oldSource.isInitializing || false
      };
    return s;
  });
}

/*
  This function sets the title of sources without titles to an empty string
*/
export function findAndUpdateEmptySourceTitle(sources) {
  return sources.map(s => {
    if (!s.title) {
      s.title = '';
    }
    return s;
  });
}

/**
 * Small function to convert string to lowercase
 * @param {*} str: String | undefined;
 * @returns String | undefined;
 */
function toLowerCase(str) {
  return str && str?.toLowerCase();
}

/**
 * Small function to match a knock presenter name to a source title/name
 * @param {*} knockName: String;
 * @param {*} source: String;
 * @returns boolean
 */
export function matchPresenterName(knockName, source) {
  const _knockName = toLowerCase(knockName);
  const _sourceTitle = toLowerCase(source.title);

  /** Exact match of presenter name and source title */
  return _knockName === _sourceTitle;
}

function isVideoOrScreenPresenter(source) {
  return isVideoSource(source) || isScreenPresenter(source);
}

function isSoloedSourceInProgram(layers, source) {
  return isSourceInProgram(layers, source._id);
}

/**
 * if we spotlight/solo a source in program and that source is a video/screen presenter
 * the sources being removed from program should not be muted
 * However, in any other cases, the sources being removed should be muted
 * @param layers
 * @param source
 * @returns boolean
 */
export function shouldMuteSourcesRemovedAfterSolo(layers, source) {
  return !(
    isVideoOrScreenPresenter(source) && isSoloedSourceInProgram(layers, source)
  );
}

export const muteSourcesRemovedFromProgram = (layers, source, dispatch) => {
  const sourcesMuteStatus = layers?.reduce((acc, layer) => {
    if (
      layer.source.type === CAMERA_PRESENTER &&
      layer.source._id !== source?._id
    ) {
      const sourceValue = getSourceValue(layer.source);
      acc[sourceValue] = true;
    }
    return acc;
  }, {});

  dispatch(setMultipleDesiredAttendeeMuteStatuses(sourcesMuteStatus));
};

/**
 * @description get the user's sources ids from the attendees list
 * @param attendeeResponse
 * @param attendeesList
 * @returns {*}
 */
export const getAttendeeSourcesIds = (attendeeResponse, attendeesList) => {
  const { ExternalUserId } = attendeeResponse.Attendee;

  const externalUserId = ExternalUserId.split('-')[0];

  return (
    attendeesList?.Attendees?.filter(
      item => item.ExternalUserId.split('-')[0] === externalUserId
    ).map(item => item.AttendeeId) ?? []
  );
};

/**
 * @description this returns all the users camera sources, unassigned sources and sources without attendeePresence
 * @param sources
 * @param attendeeResponse
 * @param attendeeList
 * @param broadcast
 * @returns {*[]}
 */
export const getAttendeeCameraSources = (
  sources,
  attendeeResponse,
  attendeeList,
  broadcast
) => {
  const cameraPresenters = sources.filter(
    source => source.type === CAMERA_PRESENTER
  );

  const unassignedCameraSources = cameraPresenters.filter(
    source => source.value === UNASSIGNED || source.disconnected
  );

  const assignedCameraSources = cameraPresenters.filter(
    source => source.value !== UNASSIGNED || !source.disconnected
  );

  const broadcastId = broadcast?._id;
  // add broadcast id to this.
  const currentDirectorValue = localStorage.getItem(
    `CURRENT_DIRECTOR_ATTENDEE-${broadcastId}`
  );

  const currentLocalDirector = assignedCameraSources.find(
    source => source.value === currentDirectorValue
  );

  const attendeeSourceIds = getAttendeeSourcesIds(
    attendeeResponse,
    attendeeList
  );

  let attendeeSources = [];

  if (attendeeSourceIds.length > 0) {
    attendeeSources = cameraPresenters.filter(source =>
      attendeeSourceIds.includes(source.value)
    );
  }

  let response = [...attendeeSources, ...unassignedCameraSources];

  if (currentLocalDirector) {
    response = [...response, currentLocalDirector];
  }
  return response;
};

/**
 * @description Returns the id of the first source that matches the user's name it will return undefined
 * @param attendeeResponse
 * @param attendeeRoster
 * @param sources
 * @returns {*}
 */
export const getDefaultSource = (attendeeResponse, attendeeRoster, sources) => {
  const { AttendeeId: attendeeId } = attendeeResponse.Attendee;

  const attendeeNameFromAttendeeRoster =
    attendeeRoster[attendeeId.replace('#content', '')];

  let currentAttendeeName;

  if (!!attendeeNameFromAttendeeRoster) {
    currentAttendeeName = attendeeNameFromAttendeeRoster.name
      ?.toLowerCase()
      .replace(/\s+/g, '');
  }
  const unassignedAttendeesName = sources.map(source => source.title);

  // Check for a full name match without case or whitespace
  let attendeeUnassignedSource = unassignedAttendeesName.find(
    item => item?.toLowerCase().replace(/\s+/g, '') === currentAttendeeName
  );

  if (attendeeUnassignedSource) {
    // get the source id
    attendeeUnassignedSource = sources.find(
      source => source.title === attendeeUnassignedSource
    )._id;
  }
  console.log(
    `getDefaultSource - ${attendeeUnassignedSource?.title} for ${currentAttendeeName}`
  );
  return attendeeUnassignedSource;
};
